import React, { useState, useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import TransitionLink from 'gatsby-plugin-transition-link'
import styled from 'styled-components'

import useLocalStorage from '../hooks/useLocalstorage'
import { SavedTripsContext } from '../utils/savedTripsContext'
import { isBrowser } from '../utils/helpers'
import {
  SavedEventMenu,
  MainEventMenu,
  Seo,
  Header,
  MobileHeader,
  MobileMenu
} from '../components'

import logo50Years from './../images/logo-50years.svg'
import logoPeturLarsen from './../images/peturlarsen-logotype.svg'

gsap.registerPlugin(ScrollTrigger)

const PageView = styled.main`
  position: relative;
`

const Logo = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 501;
  padding: 10px;
`

const MixBlendLogo = styled(Logo)`
  ${({isIndex}) => isIndex ? '' : 'mix-blend-mode: difference'};
`;

const LogoContainer = styled.div`
  width: 93px;
  height: 33px;
  position: relative;
`;

const LogoImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
`;

const Page = ({ children, title='Petur Larsen', isIndex = false, dissallowZoom, ...rest }) => {
  const [savedTrips, setSavedTrips] = useLocalStorage('petur_larsen_saved', [])

  const [isDesktopMenuOpen, setIsDesktopMenuOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isSavedEventsMenuOpen, setIsSavedEventMenuOpen] = useState(false)
  
  const [isMobile, setIsMobile] = useState(false)
  const [isLoaded, setIsLoaded]= useState(false)

  const toggleMobileMenu = () => {
    if(isMobileMenuOpen) {
      setIsSavedEventMenuOpen(false)
    }
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const toggleDesktopMenu = () => {
    if(isSavedEventsMenuOpen) {
      setIsSavedEventMenuOpen(false)
    }
    setIsDesktopMenuOpen(!isDesktopMenuOpen)
  }

  const toggleSavedEventsMenu = () => {setIsSavedEventMenuOpen(!isSavedEventsMenuOpen)}

  const closeDesktopMenu = () => setIsDesktopMenuOpen(false)
  const closeMobileMenu = () => setIsMobileMenuOpen(false)

  useEffect(() => {
    if (isBrowser()) {
      setIsMobile(window.innerWidth < 768)
    }
    setIsLoaded(true)
  }, [])

  return (
    <>
      <SavedTripsContext.Provider value={{ savedTrips, setSavedTrips }}>
        <PageView {...rest}>
          {}
          <Seo
            title={title}
            description="Welcome to Petur Larsen's Faroe Islands - 50 years anniversary"
            ogTitle="Petur Larsen - 50 years"
            ogDescription="Welcome to Petur Larsen's Faroe Islands - 50 years anniversary"
            ogImage="https://petur-larsen-faroe-islands.s3-eu-west-1.amazonaws.com/peturlarsen-standard-og-image.jpeg"
            dissallowZoom={dissallowZoom}
          />
          <Logo>
            <LogoContainer>
              <LogoImage src={logo50Years} alt="1971 - 2021" />
            </LogoContainer>
          </Logo>
          <MixBlendLogo isIndex={isIndex}>
            <TransitionLink
              to="/"
              exit={{
                trigger: ({ entry, node }) => gsap.to(node, { opacity: 0, duration: .3 }),
                length: .3
              }}
              entry={{
                trigger: ({ exit, node }) => gsap.from(node, { opacity: 0, duration: .3 }),
                delay: .3,
                length: .3
              }}
            >
              <LogoContainer>
                <LogoImage src={logoPeturLarsen} alt="Petur Larsen" />
              </LogoContainer>
              <span className="sr-only">Go to frontpage</span>
            </TransitionLink>
          </MixBlendLogo>

          <Header
            isIndex={isIndex}
            toggleSavedEventsMenu={toggleSavedEventsMenu}
            toggleFullProgram={toggleDesktopMenu}
          />

          <MainEventMenu
            isOpen={isDesktopMenuOpen}
            closeMenu={closeDesktopMenu}
            openSavedEvents={() => setIsSavedEventMenuOpen(true)}
          />

          {isLoaded && (
            <SavedEventMenu isOpen={isSavedEventsMenuOpen} closeMenu={() => setIsSavedEventMenuOpen(false)} />
          )}

          { children }
          {isMobile && <MobileMenu isOpen={isMobileMenuOpen} close={closeMobileMenu}/>}
          {isLoaded && (
            <MobileHeader
              toggleMobileMenu={toggleMobileMenu}
              isOpen={isMobileMenuOpen}
              isSavedEventsMenuOpen={isSavedEventsMenuOpen}
              closeSavedEventsMenu={() => setIsSavedEventMenuOpen(false)}
            />
          )}
        </PageView>
      </SavedTripsContext.Provider>
    </>
  )
}

export default Page
